import React from 'react';
import Field from '../../Field';
import { Radio, Check, GroupLabel } from '../../FormControls';
import {
  FREQUENCIES, FREQUENCY_UNITS, MONTHLY_ANCHORS, RECURRENCE_MODES, nthWeekdayLabel,
  nthWeekdayOf,
} from '../../../domain/recurrence';
import { weeklyHoldHelp, deletingEntriesNotice } from '../../../domain/calendarWording';
import { useDateFormat } from '../../../context/LocaleContext';
import s from './eventDrawer.module.css';
import { DateInput } from '../../RegionInputs';

// How the event repeats. The frequency controls appear only for a recurring
// event — a one-time event has nothing to say about intervals, and a temporary
// hold is weekly by definition.

const title = (v) => v.charAt(0).toUpperCase() + v.slice(1);

// The date the warning below names is the studio's short date (`useDateFormat`,
// drawn by the core from the string, so no zone moves it) — `M/D/YYYY` for a
// US studio, as MMS names it.

// The day number MMS names in "Monthly on day 14". Split, not Date-parsed: a
// local-time parse shows the previous day west of Greenwich.
const dayOfMonth = (iso) => Number(String(iso || '').split('-')[2]) || '';

export default function RecurrenceSection({
  form, set, shape, deletesEntriesAfter = null, weeklyHoldDays = 7, intervalError = null,
}) {
  const recurring = form.recurrenceMode === RECURRENCE_MODES.recurring;
  const shortDate = useDateFormat();

  return (
    <div className={s.section}>
      <GroupLabel>Recurrence</GroupLabel>

      <Radio
        name="rec" label="One-time event"
        checked={form.recurrenceMode === RECURRENCE_MODES.one_time}
        onChange={() => set('recurrenceMode', RECURRENCE_MODES.one_time)}
      />
      {/* A hold is a slot kept for a student who has not confirmed, so it has
          no meaning on an event nobody attends; MMS omits it there too. The
          number is the studio's "Weekly time slot hold duration", quoted as
          the save will use it. */}
      {shape.weeklyHold ? (
        <Radio
          name="rec" label="Temporary weekly hold"
          help={weeklyHoldHelp(weeklyHoldDays)}
          checked={form.recurrenceMode === RECURRENCE_MODES.weekly_hold}
          onChange={() => set('recurrenceMode', RECURRENCE_MODES.weekly_hold)}
        />
      ) : null}
      <Radio
        name="rec" label="Recurring event" help="Set up a repeating schedule."
        checked={recurring}
        onChange={() => set('recurrenceMode', RECURRENCE_MODES.recurring)}
      />

      {recurring ? (
        <div className={s.spacedTight}>
          <div className={s.pair}>
            <Field label="Frequency">
              <select
                className={s.input}
                value={form.frequency}
                onChange={(e) => set('frequency', e.target.value)}
              >
                {FREQUENCIES.map((f) => <option key={f} value={f}>{title(f)}</option>)}
              </select>
            </Field>
            {/* The `htmlFor`/`id` pair by hand: `Field` mints an id for a child
                it can label, and a `<span>` is not one (#383). */}
            <Field label="Repeats Every" htmlFor="event-repeat-interval" error={intervalError}>
              {/* The unit follows the frequency rather than being spelled into
                  the option labels: "Weekly" + "2 Week(s)" is how MMS says a
                  fortnight, and it has no Biweekly option (#26). */}
              <span className={s.suffixed}>
                <input
                  id="event-repeat-interval"
                  type="number" min="1" className={s.input}
                  value={form.interval}
                  onChange={(e) => set('interval', e.target.value)}
                />
                <span className={s.suffix}>{FREQUENCY_UNITS[form.frequency] || ''}</span>
              </span>
            </Field>
          </div>

          {/* MMS's second monthly control, which only a monthly rule has (#78):
              a rule anchored to a day number or to an ordinal weekday, as two
              radios under "Repeat", day-of-month the default. For a start date
              of Monday 30 November 2026:

                Repeat
                  (o) Monthly on day 30
                  ( ) Monthly on the fifth Monday

              The weekday and the ordinal are read off the form's date, so both
              labels follow it, the ordinal literally: a fifth Monday is "the
              fifth Monday", not "the last". And it means it literally: such a
              series skips every month with only four Mondays, which MMS does
              not warn about, so neither does this.
              `nthWhenTheMonthIsShort` in src/domain/recurrence.js has the
              dates. */}
          {form.frequency === 'monthly' ? (
            <div className={s.spacedTight}>
              <GroupLabel>Repeat</GroupLabel>
              <Radio
                name="monthlyAnchor"
                label={`Monthly on day ${dayOfMonth(form.startDate)}`}
                checked={(form.monthlyAnchor || MONTHLY_ANCHORS.day) === MONTHLY_ANCHORS.day}
                onChange={() => set('monthlyAnchor', MONTHLY_ANCHORS.day)}
              />
              <Radio
                name="monthlyAnchor"
                label={`Monthly on ${nthWeekdayLabel(nthWeekdayOf(form.startDate)) || 'the same weekday'}`}
                checked={form.monthlyAnchor === MONTHLY_ANCHORS.weekday}
                onChange={() => set('monthlyAnchor', MONTHLY_ANCHORS.weekday)}
              />
            </div>
          ) : null}

          <div className={s.spacedTight}>
            <Check
              label="Repeat indefinitely"
              checked={form.repeatIndefinitely}
              onChange={(e) => set('repeatIndefinitely', e.target.checked)}
            />
          </div>

          {/* A date, not a number of lessons: MMS asks when the series stops.
              `min` refuses a date before the first lesson, which would describe
              a series containing nothing; MMS greys those days out in its
              picker. */}
          {!form.repeatIndefinitely ? (
            <>
              <div className={[s.narrow, s.spacedTight].join(' ')}>
                <Field label="Repeat Until">
                  <DateInput className={s.input}
                    min={form.startDate}
                    value={form.endDate}
                    onChange={(e) => set('endDate', e.target.value)}
                  />
                </Field>
              </div>

              {/* Pulling the bound back deletes those entries, and their
                  overrides do not come back if the term is extended again. MMS
                  says so as you type, in red, under the field, in these words.
                  Not a blocker: ending a term early is a thing teachers mean.
                  Wider than the field, so the date does not break across
                  lines. */}
              {deletesEntriesAfter ? (
                <div className={s.notice}>
                  {deletingEntriesNotice(shortDate(deletesEntriesAfter))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
